import { observer } from "mobx-react-lite";
import { Button, Modal, Segment, TabPane } from "semantic-ui-react";
import { IAssessment } from "../../../../../Models/Assessment";
import { IRecipientModel } from "../../../../../Models/Recipient";
import { MedicalLearnMoreFactory } from "../Factories/MedicalLearnMoreFactory";
import { LegalLearnMoreFactory } from "../Factories/LegalLearnMoreFactory";
import { FamilyLearnMoreFactory } from "../Factories/FamilyLearnMoreFactory";
import { SpiritualLearnMoreFactory } from "../Factories/SpiritualLearnMoreFactory";
import { DocumentLibrary } from "../../../../Assessment/Documents/DocumentLibrary";
import { moduleTitles } from "../../../../Enums/TitleEnums";
import { headerNavigation as topNav, navigationButtons as navBtn } from "../../../../Enums/NavigationEnums"
import React, { useRef, useState }  from "react";
import TaskCompleter from "../TaskCompleter";
import TaskModalHeader from "./TaskModalHeader";
import ComingSoonPlaceholder from "../../../../Assessment/ComingSoonPlaceholder";
import styles from "./TaskModal.module.css";
import RenderResources from "../../../../Assessment/Resources/RenderResources";

const cdnRoute = process.env.REACT_APP_CDN_ROUTE + "/icons/";

interface TaskModalProps {
  hideAssessment: () => void;
  taskTitle: string;
  recipient: IRecipientModel;
  taskIndex: number;
  assessment: IAssessment;
  domain: string;
  caregiverId: string;
  selectedRecipientId: string;
  selectedRecipientName: string;
  setTaskTitle: (title: string) => void;
}

const TaskModal: React.FC<TaskModalProps> = (props) => {
  const {
    hideAssessment,
    taskTitle,
    recipient,
    taskIndex,
    assessment,
    domain,
    caregiverId,
    selectedRecipientId,
    selectedRecipientName,
    setTaskTitle,
  } = props;

  const topContentRef = useRef<HTMLDivElement>(null);
  const questionContentRef = useRef<HTMLDivElement>(null);

  const [activeHeaderTab, setActiveHeaderTab] = useState(topNav.TASKS.toString());
  const [taskHeader, setTaskHeader] = useState(taskTitle);
  const [infoSlideIndex, setInfoSlideIndex] = useState(taskIndex);   
  const manageTaskTitle = (index: number) => {
    const taskName = assessment.questions.find( x => x.key === assessment.infoSlideKeys[index])?.taskName;
    var pageTitle = !!taskName ? taskName.toString() : "";
    if(!!pageTitle) { 
      setTaskTitle(pageTitle)
      setTaskHeader(pageTitle); 
    }
  }


  const menuNavigationEvent= (name: string | null) => { 
    const newTabName = !!(name) ? name : topNav.TASKS;
    setActiveHeaderTab(newTabName);
    manageTaskTitle(infoSlideIndex);
  }
  const infoSlidesComplete = () => {
    return infoSlideIndex > assessment.infoSlideKeys.length - 1;
  };
  const isLastSlide = () => {
    return infoSlideIndex === assessment.infoSlideKeys.length - 1;
  }

  const onNextClick = () => {
    //Scroll to bottom of task if not already on bottom
    if (questionContentRef.current) {
      const rect = questionContentRef.current.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        // Element is not fully visible, scroll it into view
        questionContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }

    if (isLastSlide())  { 
      hideAssessment();
      scrollToTop();
    } 
    else { 
      const newIndex = infoSlideIndex + 1;
      setInfoSlideIndex(newIndex); 
      manageTaskTitle(newIndex);
      setActiveHeaderTab(topNav.TASKS.toString());
      scrollToTop();
    }
  };

  const onBackClick = () => {
    if (infoSlideIndex !== 0) { 
      const newIndex = infoSlideIndex - 1;
      setInfoSlideIndex(newIndex); 
      manageTaskTitle(newIndex);
      setActiveHeaderTab(topNav.TASKS.toString());
    } 
    else if (!infoSlidesComplete()) { hideAssessment(); }
    
    scrollToTop();
  };
  const scrollToTop = () => {
    if (topContentRef.current) {
      topContentRef.current.scrollTop = 0;
      console.debug('topContentRef.current.scrollTop', topContentRef.current.scrollTop, topContentRef.current)
    }

    topContentRef.current?.scrollIntoView({ behavior: 'smooth' });
    console.debug('topContentRef.current.scrollTop', topContentRef.current?.scrollTop, topContentRef.current)
  };


  const learnMoreRender = () => {
    const dom = infoSlidesComplete() ? moduleTitles.COMING_SOON : domain;
    switch (dom) {
      case moduleTitles.MEDICAL:
        return (
          <MedicalLearnMoreFactory infoSlideIndex={infoSlideIndex} />
        );
      case moduleTitles.LEGAL:
        return (
          <LegalLearnMoreFactory infoSlideIndex={infoSlideIndex} navigateToDocuments={() => setActiveHeaderTab(topNav.DOCUMENTS.toString())} />
        );
      case moduleTitles.FAMILY:
        return (
          <FamilyLearnMoreFactory infoSlideIndex={infoSlideIndex} />
        );
      case moduleTitles.SPIRITUAL:
        return (
          <SpiritualLearnMoreFactory infoSlideIndex={infoSlideIndex} />
        );
      default:
        return (
          <ComingSoonPlaceholder />
      );
    }
  };
  

  const modalActionsRender = () => {
    const tab = !infoSlidesComplete() ? activeHeaderTab 
                                      : isLastSlide() ? activeHeaderTab : null;
    switch (tab) {
      case topNav.TASKS:
        return (
          <>
            <div className={ styles.taskModalFooterButtonDivTask} id={styles['taskModalFooterButtonDiv']}>
                <Button className= { styles.backButton } onClick={() => onBackClick()} >
                  { (infoSlideIndex === 0 ? navBtn.RETURN_TO_OVERVIEW : navBtn.PREVIOUS_TASK) }
                </Button>
                <div className={`${styles.buttonGrpWrapper}`} >
                  <div className={`small ui basic buttons ${styles.tabButtonGroup}`} >
                    <div onClick={() => setActiveHeaderTab(topNav.LEARN.toString())} className={`small ui button ${styles.tabButton}`} >Learn More</div>
                    <div onClick={() => setActiveHeaderTab(topNav.RESOURCES.toString())} className={`small ui button ${styles.tabButton}`} >Resources</div>
                    <div onClick={() => setActiveHeaderTab(topNav.DOCUMENTS.toString())} className={`small ui button ${styles.tabButton}`} >Documents</div>
                  </div>
                </div>
                <Button className= { styles.nextButton } onClick={() => onNextClick()} >
                 { (isLastSlide() ? navBtn.RETURN_TO_OVERVIEW : navBtn.NEXT_TASK) }
               </Button>
            </div>
          </>
        );
      case topNav.LEARN:  
      case topNav.RESOURCES:
      case topNav.DOCUMENTS:
        return (
          <>
            <div className= { styles.taskModalFooterButtonDivDetails } id={styles['taskModalFooterButtonDiv']}>
                <Button className= { styles.backButton } onClick={() => setActiveHeaderTab(topNav.TASKS.toString())} >
                 { navBtn.RETURN_TO_TASK }
              </Button>
              <div className={`${styles.buttonGrpWrapper}`} >
                <div className={`small ui basic buttons ${styles.tabButtonGroup}`} >
                  <div onClick={() => setActiveHeaderTab(topNav.LEARN.toString())} className={`small ui button ${styles.tabButton}`} >Learn More</div>
                  <div onClick={() => setActiveHeaderTab(topNav.RESOURCES.toString())} className={`small ui button ${styles.tabButton}`} >Resources</div>
                  <div onClick={() => setActiveHeaderTab(topNav.DOCUMENTS.toString())} className={`small ui button ${styles.tabButton}`} >Documents</div>
                  </div>
              </div>
              <div style={{width: '8em'}}></div>
            </div>  
          </>          
        ); 
      default:
        return (
          <>    
            <div className= { styles.taskModalFooterButtonDiv } id={styles['taskModalFooterButtonDiv']}>
                <Button className= { styles.nextButton } onClick={() => onNextClick()} >
                  { navBtn.RETURN_TO_OVERVIEW }
                </Button>
            </div>
          </>          
        );
    }
  }
  

  return (
    <>
    <Modal open={true} id={styles.taskModalModal} >
      <Segment.Group className={styles.taskModalModalUi}>
        <TaskModalHeader
          hideAssessment={hideAssessment}
          taskIndex={taskIndex}
          taskTitle={taskHeader}
          activeTab={activeHeaderTab}
          menuNavigationEvent={menuNavigationEvent}
        />

        <Segment attached className={styles.taskModalContent} >
          <TabPane active={activeHeaderTab === topNav.TASKS} className={styles.taskModalFullWidthHeight} >
              <TaskCompleter
                  topContentRef={topContentRef}
                  questionContentRef={questionContentRef}
                  task={assessment}
                  selectedRecipient={recipient}
                  showTaskList={() => {}}
                  returnToOverview={hideAssessment}
                  taskIndex={taskIndex}
                  infoSlideIndex={infoSlideIndex}
                  domain={domain}
                  nextTaskHandler={onNextClick}
                />           
          </TabPane>

          <TabPane active={activeHeaderTab === topNav.LEARN} className={styles.taskModalFullWidthHeight} >
            {learnMoreRender()}
          </TabPane>

          <TabPane active={activeHeaderTab === topNav.RESOURCES} className={styles.taskModalFullWidthHeight} >
            <Segment basic className={styles.imgResourceBG} style={{ backgroundImage: `url(${cdnRoute}Paper-Texture-BG.jpg)`, }}>
              <Segment basic className={styles.segmentTabWithBGImg} >
                <RenderResources 
                  areInfoSlidesComplete={infoSlidesComplete()} 
                  infoSlideIndex={infoSlideIndex}
                  assessment={assessment}
                  domain={domain} />
              </Segment>
            </Segment>
          </TabPane>

          <TabPane active={activeHeaderTab === topNav.DOCUMENTS} className={styles.taskModalFullWidthHeight} >
            <DocumentLibrary domain={domain} caregiverId={caregiverId}  recipientId={selectedRecipientId} recipientName={selectedRecipientName} />
          </TabPane>
        </Segment>

        <Segment clearing attached='bottom' className={styles.taskModalActionFooterWrapper} >
            {modalActionsRender()}
        </Segment>  
      </Segment.Group>        
    </Modal>
    </>
  );
};

export default observer(TaskModal);
